import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {MatSort, Sort} from '@angular/material/sort';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {FormControl} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {TableControl} from '../../../../../common/models/TableControl';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {PagedRequestDTO} from '../../../../../common/models/PagedRequestDTO';
import {DisplayIcon} from 'src/app/modules/common/components';
import {NationalDrugFormularyDTO} from '../../../../models/NationalDrugFormularyDTO';
import {NationalDrugFormularyService} from '../../../../services/national-drug-formulary.service';
import {
  ConfirmationDialogComponent
} from '../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {detailsExpand} from '../../../../../common';
import {createFieldMap, getSortDirection} from '../../../../../common/util/Util';
import {SharedService} from '../../../../../common/services/shared-service';
import {UserAuthorisationService} from 'src/app/modules/common/services/user-authorisation.service';
import {EntityTable} from '../../../../../common/types/entity-table.enum';

@Component({
  selector: 'app-national-drug-formularies',
  templateUrl: './national-drug-formularies.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class NationalDrugFormulariesComponent implements OnInit, AfterViewInit {
  readonly DisplayIcon = DisplayIcon;

  nationalDrugFormularyFormControl: FormControl;
  nationalDrugFormularyDisplayedColumns: string[] = [
    'icons',
    'nationalDrugFormularyId',
    'name',
    'formularyClass',
    'startDate',
    'endDate'
  ];
  nationalDrugFormularyTableControl: TableControl;
  nationalDrugFormularyDataSource: NationalDrugFormularyDTO[];
  nationalDrugFormularyExpandedRow: NationalDrugFormularyDTO | null;

  isConfirmed = false;
  dateFormat = DateUtil.dateFormat;
  dateTimeFormat = DateUtil.dateTimeFormat;
  length = 100;
  pageSize = 10;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  @ViewChild('matSort', {static: false}) tableSort: MatSort;
  @ViewChild('nationalDrugFormularySort', {static: false}) nationalDrugFormularySort: MatSort;
  @ViewChild('nationalDrugFormularyPaginator', {static: false}) nationalDrugFormularyPaginator: MatPaginator;
  @ViewChild('nationalDrugFormularySearch') private nationalDrugFormularySearch: ElementRef;

  constructor(
    private router: Router,
    public dialog: MatDialog,
    private sharedService: SharedService,
    private nationalDrugFormularyService: NationalDrugFormularyService,
    private userAuthorisationService: UserAuthorisationService
  ) {
    this.nationalDrugFormularyFormControl = new FormControl('');
    this.nationalDrugFormularyTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'nationalDrugFormularyId',
      'desc',
      'name',
      ''
    );
  }

  get isAuthorisedToDelete(): boolean {
    return this.userAuthorisationService.hasRoleOperationsManager();
  }

  ngOnInit(): void {
    this.getListOfNationalDrugFormularies(this.nationalDrugFormularyTableControl);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.nationalDrugFormularySearch.nativeElement.focus(), 0);
  }

  getListOfNationalDrugFormularies(tableControl: TableControl): void {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblNationalDrugFormulary;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = getSortDirection(
      tableControl.sortDirection
    );
    pagedRequestDTO.fieldMap = createFieldMap(
      tableControl.filterField,
      tableControl.filterValue
    );

    this.nationalDrugFormularyService.getListOfNationalDrugFormularies(pagedRequestDTO).subscribe(
      (response) => {
        this.nationalDrugFormularyDataSource = response.resultList;
        this.nationalDrugFormularyTableControl.length = response.totalRecords;
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    );
  }

  pageChanged($event: PageEvent) {
    this.nationalDrugFormularyTableControl.pageNumber = $event.pageIndex;
    this.nationalDrugFormularyTableControl.pageSize = $event.pageSize;
    this.getListOfNationalDrugFormularies(this.nationalDrugFormularyTableControl);
  }

  sortData($event: Sort) {
    if (this.nationalDrugFormularyPaginator != null) {
      this.nationalDrugFormularyPaginator.firstPage();
    }
    this.nationalDrugFormularyTableControl.pageNumber = 0;
    this.nationalDrugFormularyTableControl.sortField = $event.active;
    this.nationalDrugFormularyTableControl.sortDirection = $event.direction;
    this.getListOfNationalDrugFormularies(this.nationalDrugFormularyTableControl);
  }

  onInputChangeFilterData(filterField: string) {
    if (this.nationalDrugFormularyPaginator != null) {
      this.nationalDrugFormularyPaginator.firstPage();
    }
    this.nationalDrugFormularyTableControl.pageNumber = 0;
    this.nationalDrugFormularyTableControl.filterField = filterField;
    this.nationalDrugFormularyTableControl.filterValue = this.nationalDrugFormularyFormControl.value;
    this.getListOfNationalDrugFormularies(this.nationalDrugFormularyTableControl);
  }

  deleteEntry(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isAuthorisedToDelete) {
        return;
      }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: 'Are you sure you wish to delete this entity?'
    });

    dialogRef.afterClosed().subscribe((result) => {
      const isConfirmed = result;

      if (isConfirmed) {
        this.nationalDrugFormularyService.deleteNationalDrugFormulary(id).subscribe(
          () => {
            this.sharedService.openSnackBar(`National Drug Formulary ${id} deleted`, 'Dismiss');
            this.getListOfNationalDrugFormularies(this.nationalDrugFormularyTableControl);
          },
          (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        );
      }
    });
  }

  editEntry(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate(['/pharmacy-management/edit-national-drug-formulary/' + id]);
  }

}
